import {
    Component,
    computed,
    effect,
    inject,
    Injector,
    Input,
    OnInit,
    Renderer2,
    signal,
} from '@angular/core';

import {UpgradeUiViewOneDirective, UpgradeUiViewTwoDirective} from 'src/ajs-to-angular';
import {ToolbarEventsService} from 'src/app/core/services/toolbar-events/toolbar-events.service';
import {SearchBarComponent} from 'src/app/core/components/generalized-toolbar/search-bar/search-bar.component';
import {DevLogger} from 'src/app/shared/dev-logger';
import {ScreenOrientationService} from 'src/app/core/services/screen-orientation.service';
import {distinctUntilChanged} from 'rxjs/internal/operators/distinctUntilChanged';
import {CustomForm, CustomFormGroup} from '@tilby/tilby-ui-lib/components/tilby-magic-form';

import {
    BarcodeEvent,
    BarcodeManagerService,
    EntityDictionariesStoreService,
    EnvironmentInfoService,
    RIGHT_SIDENAV_PORTAL_DATA_TOKEN,
    RightSidenavService
} from 'src/app/core';

import {TranslateService} from '@ngx-translate/core';
import {ComponentPortal} from '@angular/cdk/portal';
import {
    CashregisterTopbarSettingsComponent
} from './components/cashregister-topbar-settings/cashregister-topbar-settings.component';
import { Categories, Channels, Departments, Items } from 'tilby-models';
import {GeneralInputButton, MenuItem} from '@tilby/tilby-ui-lib/models';
import {CommonModule} from "@angular/common";
import {CashregisterMobileViewComponent} from "./cashregister-mobile-view/cashregister-mobile-view.component";

import {
    documentPrinter,
    leanPMS,
} from "app/ajs-upgraded-providers";

import {CashregisterKeyboardComponent} from "./components";
import {
    TilbyDraggableBottomSheetComponent
} from "./components/cashregister-showcase/tilby-draggable-bottom-sheet/tilby-draggable-bottom-sheet.component";

import {
    ActiveSaleService,
    ActiveSaleStoreService,
    CustomerDisplayManagerService,
    QuickCoupon
} from "./services";

import {
    subscribeInComponent
} from "src/app/shared/angular-utils";

import {DualService} from 'src/app/dual.service';
import {BehaviorSubject, filter, first, map, switchMap, tap} from "rxjs";
import {SharedModule} from "../../shared/shared.module";
import {
    AddCashMovementDialogService,
    DailyClosingDialogService,
    GenericListDialogService,
    MagoOrdersDialogService,
    PrinterErrorFiscalDialogService,
    WaitDialogService
} from 'src/app/dialogs';
import {
    CashregisterPaymentsMobileViewComponent
} from "./cashregister-payments-mobile-view/cashregister-payments-mobile-view.component";
import {SelectTypeDailyClosingDialogService} from 'src/app/dialogs/select-type-daily-closing-dialog';
import {
    FiscalPrinterDashboardDialogService
} from 'src/app/dialogs/fiscal-printer-dashboard-dialog';
import {ObjDataData, Today} from '../dashboard/models';
import {
    alignDataPrinters,
    DashboardDataManagerService,
    getPayments,
    getToday,
    getWeatherIcon
} from 'src/app/features/dashboard';
import {
    TilbyQuickCouponButtonsComponent
} from "./components/cashregister-keyboard/tilby-quick-coupon-buttons/tilby-quick-coupon-buttons.component";
import {TilbyCurrencyPipe} from '@tilby/tilby-ui-lib/pipes/tilby-currency';
import {KeyboardParentToAddNewItemToSaleComponent} from './keyboard-parent-to-add-new-item-to-sale';

import { MathUtils } from 'src/app/shared/utils';
import { BarcodeComponent } from 'src/app/shared/components';

import {
    EposFiscalDeviceService
} from '../printers/services/epos-fiscal-device.service';

export type ItemsByCategory = { [key: number | string]: Items[] };

@Component({
    standalone: true,
    selector: 'app-cashregister',
    templateUrl: './cashregister.component.html',
    styleUrls: ['./cashregister.component.scss'],
    providers: [],
    host: {
        class: 'tw-h-full tw-block'
    },
    imports: [
        BarcodeComponent,
        CashregisterKeyboardComponent,
        CashregisterMobileViewComponent,
        CashregisterPaymentsMobileViewComponent,
        CommonModule,
        SharedModule,
        TilbyDraggableBottomSheetComponent,
        TilbyQuickCouponButtonsComponent,
        UpgradeUiViewOneDirective,
        UpgradeUiViewTwoDirective,
    ]
})
export class CashregisterComponent extends KeyboardParentToAddNewItemToSaleComponent implements OnInit {
    private readonly addCashMovementDialogService = inject(AddCashMovementDialogService);
    private readonly dailyClosingDialogService = inject(DailyClosingDialogService);
    private readonly customerDisplayManager = inject(CustomerDisplayManagerService);
    private readonly dashboardDataManagerService = inject(DashboardDataManagerService);
    private readonly documentPrinter = inject(documentPrinter);
    private readonly dualService = inject(DualService);
    private readonly fiscalPrinterDashboardDialog = inject(FiscalPrinterDashboardDialogService);
    private readonly environmentInfoService = inject(EnvironmentInfoService);
    protected readonly genericListDialogService = inject(GenericListDialogService);
    private readonly injector = inject(Injector);
    private readonly leanPMS = inject(leanPMS);
    private readonly magoOrdersDialog = inject(MagoOrdersDialogService);
    private readonly printerErrorFiscalService = inject(PrinterErrorFiscalDialogService);
    private readonly renderer = inject(Renderer2);
    private readonly rightSidenavService = inject(RightSidenavService<any>);
    private readonly screenOrientationService = inject(ScreenOrientationService);
    private readonly toolbarEventsService = inject(ToolbarEventsService);
    private readonly translateService = inject(TranslateService);
    private readonly waitDialogService = inject(WaitDialogService);

    @Input({required: true}) items!: ItemsByCategory;
    @Input({required: true}) categories!: Categories[];
    @Input({required: true}) departments!: Departments[];
    @Input({required: true}) deliveryChannels!: Channels[];

    // CHECK MOBILE AND VERTICAL PORTRAIT
    protected isMobilePortrait = this.screenOrientationService.isMobilePortrait;
    protected isMobile = this.cashregisterStateService.isMobile;
    protected isInPaymentsView = signal(false);
    protected showKeyboard = true;

    private priceLists: MenuItem[] = [];
    private changePricelistIcon = new BehaviorSubject(this.priceList());
    private isAddingBarcodeItem = false;
    private mainKeyboardStatus = this.cashregisterStateService.sideKeyboard;
    private isActiveSale = this.activeSaleService.isActiveSale;
    private priceListName = computed(() => this.configurationManagerService.getPreference(`price_list_${this.priceList()}_name`) || `${this.translateService.instant('CASHREGISTER.TOPBAR.PRICE_LIST_START')} ${this.priceList()}`);

    private readonly isCheckStockEnabled = !!this.configurationManagerService.getPreference('cashregister.check_stock');
    private readonly isPriceListUpdateEnabled = !this.configurationManagerService.getPreference("cashregister.disable_price_list_apply_prompt");
    private readonly isSwitchPriceListEnabled = !!this.configurationManagerService.getPreference('cashregister.enable_switch_price_list');
    private readonly showOpenDrawer = !this.configurationManagerService.isFunctionEnabledOptin('cashregister.hide_open_drawer');

    protected readonly isAccessibility = signal(!!this.configurationManagerService.getPreference('general.simplified_accessibility'));

    private getAccessibilityString = this.isAccessibility()
        ? (string: string) => signal(`CASHREGISTER.TOPBAR.${string}`)
        : () => undefined;

    protected isLandscape = computed(() => !!this.screenOrientationService.getOrientation().includes('landscape'));

    protected mainKeyboard = computed(() => !this.cashregisterStateService.sideKeyboard());

    // START - QUICK COUPONS
    protected quickCouponButtons = this.saleUtilsService.getConfiguredQuickCoupons()
        .map(quickCoupon => (<GeneralInputButton>{
            isIt: signal(true),
            name: quickCoupon.name,
            label: signal(quickCoupon.name),
            label2: signal(this.injector.get(TilbyCurrencyPipe).transform(quickCoupon.value)),
            class: 'tw-w-full tw-p-0 tw-rounded-none',
            click: () => this.addQuickCoupon(quickCoupon),
        })) || [];

    protected addQuickCoupon(coupon:QuickCoupon){
        this.log(`Clicked ${coupon.name}`,coupon);
        const targetDepartment = this.departments.find(department => department.id == coupon.department_id );

        if(targetDepartment) {
            this.activeSaleService.addDynamicItemToSale(targetDepartment, MathUtils.round(coupon.value), {
                type: 'coupon',
                quantity: -1,
                name: coupon.name
            });
        } else {
            this.alertDialogService.openDialog({data:{messageLabel:'CASHREGISTER.SHOWCASE.QUICK_COUPON_MISSING_DEPARTMENT'}});
        }
    };
    // END - QUICK COUPONS

    private wasOpenedBySelection=false;
    protected keyboardIsOpen$ = this.cashregisterStateService.keyboardIsOpen$;
    protected keyboardIsOpenBySelection$ = this.cashregisterStateService.keyboardIsOpenBySelection$.pipe(
        distinctUntilChanged(),
        tap((toggleBySelection)=>this.log('SELECTION CLICKED VALUE',toggleBySelection)),
        switchMap(toggleBySelection =>
            this.keyboardIsOpen$.pipe(
                first(),
                map(wasOpen => (this.wasOpenedBySelection&&!toggleBySelection)
                        ?false
                        :!wasOpen&&toggleBySelection
                    ),
                tap(toggleBySelection => this.wasOpenedBySelection=toggleBySelection),
                distinctUntilChanged(),
            )
        ),
    );

    private log(...args: any[]) {
        DevLogger.debug('[ CashregisterComponent ]', ...args);
    }

    // START - LIFECYCLE
    constructor() {
        super();

        effect(() => {
            this.changePricelistIcon.next(this.priceList());
        });

        this.subscriptions();
        this.priceList.set(this.activeSaleService?.currentSale?.sale_customer?.default_pricelist || this.saleUtilsService.getCurrentPriceList());

        effect(() => {
            // Dependencies
            [
                this.mainKeyboardStatus(),
                this.isActiveSale(),
                this.isLandscape(),
                this.isInPaymentsView()
            ];

            this.createButtonsPanelsToolbar();
        });
    }
    ngOnInit(): void {
        this.setToolbar();
        this.priceLists = this.getPriceLists();
        this.setCashregisterState();
        this.cashregisterStateService.keyboardIsOpenBySelection$.next(false);
        this.setupHIDPassthrough();
    }

    private async setupHIDPassthrough() {
        try {
            const { client_uuid } = await this.environmentInfoService.getDeviceMeta();
    
            if(!client_uuid) {
                return;
            }
    
            const hidPrinterId = Number(this.configurationManagerService.getPreference(`cashregister.hid_passthrough.printer_id.${client_uuid}`));
    
            if(!hidPrinterId) {
                return;
            }
    
            const targetPrinter = await this.entityManagerService.printers.fetchOneOffline(hidPrinterId);
    
            if(targetPrinter?.driver !== 'epson' || !targetPrinter?.ip_address) {
                return;
            }

            const ePosFiscalDeviceService = this.injector.get(EposFiscalDeviceService)

            const ipAddress = targetPrinter.ip_address;

            await ePosFiscalDeviceService.openScanner(ipAddress);
        } catch(err: any) {
            this.log("Error during setup of HID passthrough", err);
        }
    }

    ngOnDestroy(): void {
        this.toolbarEventsService.centralToolbarComponent$.next(SearchBarComponent);
    }

    // END - LIFECYCLE

    private setToolbar() {
        this.setModuleTitleOnToolbar();
        this.setToolbarSearch();
    }

    private getPriceLists() {
        const priceLists = [];

        for (let idx = 1; idx <= 10; idx++) {
            // If the price list is hidden, skip it
            if (this.configurationManagerService.getPreference(`price_list_${idx}_hide`)) {
                continue;
            }

            priceLists.push({
                index: idx,
                value: this.configurationManagerService.getPreference(`price_list_${idx}_name`) || `${this.translateService.instant('CASHREGISTER.TOPBAR.PRICE_LIST_START')} ${idx}`
            });
        }

        return priceLists;
    }

    private setCashregisterState(){
        this.cashregisterStateService.initState({
            items:this.items,
            categories:this.categories,
            departments:this.departments,
            deliveryChannels:this.deliveryChannels
        })
    }

    // START - TOOLBAR SETTINGS METHODS
    private setModuleTitleOnToolbar() {
        this.toolbarEventsService.moduleTitle.next("CASHREGISTER");
    }
    private setToolbarSearch() {
        this.resetSearchBar();
        this.toolbarEventsService.searchBarIconFilled$.next('close');
        this.toolbarEventsService.searchBarAction$.next({ type: 'toolbar.search_barcode', barcode: '' });
        this.toolbarEventsService.searchBarActionFilled$.next({ type: 'toolbar.clear_search_bar' });
    }

    async selectPriceList() {
        const answer = await this.genericListDialogService.openDialog({
            data: {
                title: { label: 'DIALOG.PRICE_LIST_DIALOG.TITLE' },
                list: this.getPriceLists().map(({ index, value }) => ({
                    name: value,
                    value: index,
                    isSelected: this.priceList() == index
                })),
                showCheckbox: true
            }
        });

        if (!answer) {
            return;
        }

        this.onPriceListSelect(answer);
    }



    async createButtonsPanelsToolbar() {
        if(this.isInPaymentsView()) {
            return;        
        }

        const isLeanSetup = signal(await this.leanPMS.isSetup());
        const buttonWidthClass = this.isAccessibility() ? 'tw-w-[130px]' : '';
        const isAddCashMovementsEnabled = signal(this.configurationManagerService.isUserPermitted('cash_withdrawal') || this.configurationManagerService.isUserPermitted('cash_deposit'));

        const buttons: GeneralInputButton[] = [
            {
                isIt: signal(this.environmentInfoService.isAppleMobile()),
                isAccessibility: this.isAccessibility,
                name: 'search_sale_by_terminal_barcode',
                iconType: 'symbols',
                icon: signal('barcode'),
                label: this.getAccessibilityString('BARCODE'),
                click: () => this.onBarcodeScannedAppleMobile(),
                class: buttonWidthClass
            },
            {
                isIt: isLeanSetup,
                isAccessibility: this.isAccessibility,
                name: 'hotel',
                icon: signal('hotel'),
                label: this.getAccessibilityString('PMS_ROOM_SERVICE_ACCESSIBILITY'),
                click: () => this.openPmsLeanDialog(),
                class: buttonWidthClass
            },
            {
                isIt: signal(this.isSwitchPriceListEnabled),
                isAccessibility: this.isAccessibility,
                name: 'local_offer',
                icon: signal('local_offer'),
                click: (_event: any, data: MenuItem) => data.index && this.onPriceListSelect(data.index),
                isMenu: true,
                label: () => this.priceListName(),
                menuParams: { selected: this.changePricelistIcon, items: this.priceLists, showSelectedOnMainButton: true },
                class: buttonWidthClass
            },
            {
                isIt: this.showOpenDrawer ? signal(!this.mainKeyboardStatus() || this.isActiveSale()) : signal(false),
                isAccessibility: this.isAccessibility,
                name: 'eject',
                icon: signal('eject'),
                label: this.getAccessibilityString('OPEN_DRAWER_ACCESSIBILITY'),
                click: () => this.openCashDrawer(),
                class: buttonWidthClass
            },
            {
                isIt: signal(true),
                isAccessibility: this.isAccessibility,
                name: 'settings_backup_restore',
                icon: signal('settings_backup_restore'),
                label: this.getAccessibilityString('DAILY_CLOSING_ACCESSIBILITY'),
                click: () => this.dailyClosing(),
                class: buttonWidthClass
            },
            {
                isIt: signal(this.isCheckStockEnabled),
                isAccessibility: this.isAccessibility,
                name: 'widgets',
                icon: signal('widgets'),
                label: this.getAccessibilityString('CHECK_STOCK_ACCESSIBILITY'),
                click: () => this.openStockDialog(),
                class: buttonWidthClass
            },
            {
                isIt: signal(true),
                isAccessibility: this.isAccessibility,
                name: 'add_cash_movements',
                icon: signal('swap_vertical_circle'),
                label: this.getAccessibilityString('ADD_CASH_MOVEMENT'),
                click: () => this.addCashMovement(),
                class: buttonWidthClass
            },
            {
                isIt: signal(true),
                isAccessibility: this.isAccessibility,
                name: 'settings',
                icon: signal('settings'),
                label: this.getAccessibilityString('SETTINGS.TITLE'),
                click: () => this.settings(),
                class: buttonWidthClass
            }
        ]

        const panels: GeneralInputButton[] = [
            {
                isIt: signal(this.environmentInfoService.isAppleMobile()),
                name: 'search_sale_by_terminal_barcode',
                iconType: 'symbols',
                label: signal('CASHREGISTER.TOPBAR.BARCODE'),
                icon: signal('barcode'),
                click: () => this.onBarcodeScannedAppleMobile()
            },
            {
                isIt: isLeanSetup,
                name: 'hotel',
                icon: signal('hotel'),
                label: signal('CASHREGISTER.TOPBAR.PMS_ROOM_SERVICE'),
                click: () => this.openPmsLeanDialog()
            },
            {
                isIt: signal(this.isSwitchPriceListEnabled),
                name: 'local_offer',
                icon: signal('local_offer'),
                label: signal('CASHREGISTER.TOPBAR.PRICE_LIST_START'),
                iconLabel: () => this.priceList().toString(),
                click: () => this.selectPriceList()
            },
            {
                isIt: this.showOpenDrawer ? signal(!this.mainKeyboardStatus() || this.isActiveSale()) : signal(false),
                name: 'eject',
                icon: signal('eject'),
                label: signal('CASHREGISTER.TOPBAR.OPEN_DRAWER'),
                click: () => this.openCashDrawer(),
            },
            {
                isIt: signal(true),
                name: 'settings_backup_restore',
                icon: signal('settings_backup_restore'),
                label: signal('CASHREGISTER.TOPBAR.DAILY_CLOSING'),
                click: () => this.dailyClosing()
            },
            {
                isIt: signal(this.isCheckStockEnabled),
                name: 'widgets',
                icon: signal('widgets'),
                label: signal('CASHREGISTER.TOPBAR.CHECK_STOCK'),
                click: () => this.openStockDialog(),
            },
            {
                isIt: isAddCashMovementsEnabled,
                name: 'add_cash_movements',
                icon: signal('swap_vertical_circle'),
                label: signal('CASHREGISTER.TOPBAR.ADD_CASH_MOVEMENT'),
                click: () => this.addCashMovement()
            },
            {
                isIt: signal(true),
                name: 'settings',
                icon: signal('settings'),
                label: signal('CASHREGISTER.TOPBAR.SETTINGS.TITLE'),
                click: () => this.settings()
            }
        ]

        const divGeneralizedToolbar = document.getElementById("generalized-toolbar");
        const divSearchBar = document.getElementById("search-bar");
        const divLeftToolbar = document.getElementById("left-toolbar");

        if(!divGeneralizedToolbar || !divSearchBar || !divLeftToolbar) {
            return;
        }

        const widthGeneralizedToolbar = divGeneralizedToolbar?.offsetWidth;
        const widthLeftToolbar = divLeftToolbar?.offsetWidth;
        const widthSearchBar = divSearchBar?.offsetWidth;

        const space = widthGeneralizedToolbar! - (widthLeftToolbar! + widthSearchBar!);

        let numberOfElements = 0;

        if (!this.isAccessibility) {
            // Only icons
            numberOfElements = Math.floor(space / 48);
        } else {
            // Icons with label
            numberOfElements = Math.floor(space / 130);
        }

        const buttonsShow = buttons.filter((button) => button.isIt());
        const panelsShow = panels.filter((panel) => panel.isIt());

        let barButtons: GeneralInputButton[] = [];
        let panelButtons: GeneralInputButton[] = [];

        const buttonsLength = buttonsShow.length;

        let hasPanels = false;
        let checkNumber = buttonsLength;

        if (numberOfElements <= 0) {
            numberOfElements = 1;
        }

        if (buttonsLength > numberOfElements) {
            checkNumber = numberOfElements - 1;
            hasPanels = true;
        }

        barButtons = buttonsShow.slice(0, checkNumber);

        if(hasPanels) {
            panelButtons = panelsShow.slice(checkNumber);
        }

        this.toolbarEventsService.buttons$.next({
            barButtons,
            panelButtons
        });
    }

    ngAfterViewInit() {
        this.createButtonsPanelsToolbar();
    }

    private onPriceListSelect(priceList: number | undefined) {
        if(!this.isSwitchPriceListEnabled || !priceList) {
            return;
        }

        this.changePricelist(priceList);
    }

    private resetSearchBar() {
        this.toolbarEventsService.searchBarValue$.next('');
        this.environmentInfoService.isCameraBarcodeSupported() ? this.toolbarEventsService.searchBarIcon$.next('photo_camera') : this.toolbarEventsService.searchBarIcon$.next('');
        this.cashregisterStateService.isShowSearch$.next({isSearch: false, searchValue: ''});
    }

    private search(searchValue: string | undefined) {
        searchValue ? this.cashregisterStateService.isShowSearch$.next({isSearch: true, searchValue: searchValue}) : this.cashregisterStateService.isShowSearch$.next({isSearch: false, searchValue: ''});
    }

    private async openCashDrawer() {
        this.log('OPEN CASH DRAWER');
        await this.activeSaleService.verifyPrinterDocumentDataAndSelect();

        try {
            await this.documentPrinter.openCashDrawer(this.activeSaleService.printerDocumentData);
        } catch(error) {
            this.printerErrorFiscalService.openDialog({data: {error, options: {printerId: this.activeSaleService.printerDocumentData?.printer.id}}});
        }
    }

    private async addCashMovement() {
        this.log('ADD CASH MOVEMENT');
        await this.addCashMovementDialogService.openDialog();
    }

    private async openStockDialog() {
        this.log('OPEN STOCK');

        if(this.configurationManagerService.isMagoEnabled()) {
            this.magoOrdersDialog.openDialog({ mode: 'stock' });
            return;
        }
        
        this.openDialogsService.openStockDialog<string, CustomFormGroup<CustomForm<any>>, any>({
            data: {
                title: 'Open Stock',
                form: this.cashregisterStateService.createCashregisterStockForm()
            }
        });
    }

    private async openPmsLeanDialog() {
        const isLeanSetup = await this.leanPMS.isSetup();

        if(isLeanSetup) {
            this.leanPMS.openDialog();
        }
    }

    private async loadDataForDailyClosing() {
        const dashboardData = await this.dashboardDataManagerService.fetchData();

        // Some of the requests may have failed: the caller shows the daily reading error
        if (!dashboardData.today_dashboard?.cash_movements?.total) {
            throw 'DASHBOARD_DATA_NOT_AVAILABLE';
        }

        const printers = this.dashboardDataManagerService.getMergedResources(dashboardData.today_printers);

        const current_cash_movements = dashboardData.today_dashboard.cash_movements;
        const current_cash_in_register = current_cash_movements.total.income.amount + current_cash_movements.total.outcome.amount;

        const weather_icon = getWeatherIcon(dashboardData);

        const payments = getPayments(dashboardData, this.translateService.instant('DASHBOARD.PREPAID_CARDS'));

        const today :Today = getToday(dashboardData, payments, current_cash_movements, current_cash_in_register, weather_icon);

        const data : { printers: any; today: Today; alignedPrinters?: any } = {printers: printers, today: today }

        const alignedPrinters = alignDataPrinters(data as ObjDataData, this.cashregisterStateService.deliveryChannels);

        data.alignedPrinters = alignedPrinters;

        return data;
    }

    private async dailyClosing() {
        const action = await this.injector.get(SelectTypeDailyClosingDialogService).openDialog({ saleName: this.activeSaleService.currentSale.name }).then(res => res?.typeAction);

        switch (action) {
            case 'LG':
                const voidPromise = new Promise(async (resolve, reject) => {
                    try {
                        const data: any = await this.loadDataForDailyClosing();
                        resolve(null);
                        await this.fiscalPrinterDashboardDialog.openDialog({ data: data, disableClose: true });
                    } catch (error) {
                        resolve(error);
                        this.alertDialogService.openDialog({ data: { messageLabel: this.translateService.instant('DIALOG.SELECT_TYPE_DAILY_CLOSING.DAILY_READING_ERROR') } });
                    } finally {
                    }
                });

                await this.waitDialogService.openDialog({ data: { message: 'DASHBOARD.WAIT_UPDATING_DATA', promise: voidPromise } });
                break;
            case 'CG':
                await this.activeSaleService.verifyPrinterDocumentDataAndSelect();
                const printer = this.activeSaleService.printerDocumentData?.printer;

                if (printer) {
                    await this.dailyClosingDialogService.openDialog({ data: printer });
                    this.activeSaleService.resetDocumentData();
                }
                break;
        }
    }

    private changePricelist(priceList: number) {
        if(priceList === this.priceList()) {
            return;
        }

        this.priceList.set(priceList);
        this.saleUtilsService.updateCurrentPriceList(priceList);
        this.resetSearchBar();

        if (this.isPriceListUpdateEnabled) {
            this.cashregisterStateService.pricelistChangedEvent.next();
        }
    }
    private settings() {
        this.log('SETTINGS');
        const portalInjector = Injector.create({
            providers: [{
                provide: RIGHT_SIDENAV_PORTAL_DATA_TOKEN,
                useValue: {sale: null, gridRef: null}
            }],
        });
        this.rightSidenavService.componentPortal$.next(new ComponentPortal(CashregisterTopbarSettingsComponent, null, portalInjector));
        this.rightSidenavService.isOpen = true;
    }

    // END - TOOLBAR SETTINGS METHODS

    private subscriptions() {
        subscribeInComponent(ActiveSaleService.activeSaleEvents$, (event) => {
            switch (event.event) {
                case 'use-pricelist':
                    const data = event.data;
                    const newPriceList = data.priceList === 'default' ? this.saleUtilsService.getCurrentPriceList() : data.priceList;

                    if(!newPriceList) {
                        return;
                    }

                    if (newPriceList !== this.priceList()) {
                        this.priceList.set(newPriceList);

                        if(data.skipQuestion) {
                            this.activeSaleService.applyPriceList(this.priceList());
                        } else if (!data.skipNotify) {
                            this.cashregisterStateService.pricelistChangedEvent.next();
                        }
                    }
                break;
                case 'reset-pricelist':
                    this.changePricelist(this.saleUtilsService.getDefaultPriceList());
                break;
            }
        });

        subscribeInComponent(
            this.cashregisterStateService.addItemToSale$.pipe(filter(() => this.mainKeyboard())),
            (data) => data.item && this.addItemToSale(data.item, data.combination_id, data.quantity, data.barcode)
        );

        subscribeInComponent(
            this.cashregisterStateService.addNewItemToSale$.pipe(filter(() => this.mainKeyboard())),
            () => this.addItemToSaleHandler('sale')
        );

        subscribeInComponent(
            this.cashregisterStateService.addDynamicCategoryItemToSale$.pipe(filter(() => this.mainKeyboard())),
            (data) => this.addDynamicCategoryItemToSale(data.category, data.price)
        );

        subscribeInComponent(BarcodeManagerService.barcodeEvents$.pipe(
            filter(() => !this.activeSaleService.paymentInProgress())
        ), (event) => {
            switch(event.type) {
                case 'item':
                    if(!this.isAddingBarcodeItem) {
                        this.isAddingBarcodeItem = true;
                        this.onBarcodeItem(event).finally(() => this.isAddingBarcodeItem = false);
                    }
                break;
                case 'sale':
                    this.onBarcodeSale(event);
                break;
                case 'network-display':
                    this.onBarcodeNetworkDisplay(event);
                break;
            }
        });

        subscribeInComponent(this.dualService.currentRoute$, (currentRoute) => {
            this.isInPaymentsView.set(currentRoute.includes("payments"));
            this.showKeyboard = !this.isInPaymentsView();
        });

        subscribeInComponent(
            ActiveSaleStoreService.saleUpdateError$, (error) => this.alertDialogService.openDialog({ data: { messageLabel: `CASHREGISTER.ACTIVE_SALE.${error}` } })
        );

        subscribeInComponent(ActiveSaleStoreService.saleUpdateNotification$, (e) => {
            if(e.important) {
                this.alertDialogService.openDialog({ data: { messageLabel: `CASHREGISTER.ACTIVE_SALE.${e.event}` } });
            } else {
                this.openDialogsService.openSnackBarTilby(`CASHREGISTER.ACTIVE_SALE.${e.event}`, 'MISC.OK', { duration: 5000 });
            }
        });

        subscribeInComponent(
            EntityDictionariesStoreService.stockUpdates$, async (stockMessage) => {
                if (this.configurationManagerService.getPreference('cashregister.disable_stock_notifications')) {
                    return;
                }

                let toastMessage: string | undefined;

                switch (stockMessage.available) {
                    case 'available': case 'producible':
                        break;
                    case 'alert':
                        toastMessage = this.translateService.instant('CASHREGISTER.SHOWCASE.ITEM_STOCK_LOW', { value: stockMessage.name });
                        break;
                    case 'unavailable':
                        toastMessage = this.translateService.instant('CASHREGISTER.SHOWCASE.ITEM_STOCK_ZERO', { value: stockMessage.name });
                        break;
                    default:
                        break;
                }

                if (!toastMessage) {
                    return;
                }

                if (stockMessage.item_id) {
                    const item = await this.entityManagerService.items.fetchOneOffline(stockMessage.item_id);

                    if (item?.manual_production !== true) {
                        this.openDialogsService.openSnackBarTilby(toastMessage!, 'MISC.OK', { duration: 0 })
                    }
                } else {
                    this.openDialogsService.openSnackBarTilby(toastMessage, 'MISC.OK', { duration: 0 })
                }
            }
        )

        subscribeInComponent(this.toolbarEventsService.events, (event) => {
            switch(event.type) {
                case 'toolbar.clear_search_bar':
                    return this.resetSearchBar();
                case 'toolbar.search':
                    if (event.search) {
                        return this.search(event.search);
                    }
                    break;
                case 'toolbar.search_barcode':
                    if (event.barcode) {
                        return this.onBarcodeScanned(event.barcode);
                    }    
                    break;
                default:
                    break;

            }
        });
    }
    public async onBarcodeNetworkDisplay(event: BarcodeEvent) {
        if (event.type !== 'network-display') {
            return;
        }

        try {
            await this.customerDisplayManager.configureNetworkDisplay(event.address, event.version);
            this.openDialogsService.openSnackBarTilby('CASHREGISTER.SHOWCASE.NETWORK_DISPLAY_QRCODE_SUCCESS', 'MISC.OK', { duration: 5000 });
        } catch (err) {
            this.alertDialogService.openDialog({ data: { messageLabel: 'CASHREGISTER.SHOWCASE.NETWORK_DISPLAY_QRCODE_ERROR' } });
        }
    }

    onBarcodeScannedAppleMobile() {
        if (this.environmentInfoService.isAppleMobile()) {
            const barcodeInput = this.renderer.selectRootElement('#barcodeInput');
            barcodeInput.focus();
            barcodeInput.value = '';
        }
    }

    protected onBarcodeScanned(barcodeInput: string) {
        BarcodeManagerService.barcodeInput.next(barcodeInput);
    }

    protected async onBarcodeItem(barcodeEvent: BarcodeEvent) {
        //Make sure it's an item event
        if(barcodeEvent.type !== 'item') {
            return;
        }

        // Avoid adding items when a payment is in progress
        if (this.isInPaymentsView()) {
            return;
        }

        const foundData = barcodeEvent.itemData
        const foundArray = foundData.data;

        if (!foundArray.length) {
            if(this.configurationManagerService.getPreference('cashregister.error_sound_on_barcode_not_found')) {
                new Audio('assets/sounds/error.mp3').play();
            }

            if (this.configurationManagerService.getPreference('cashregister.add_item_on_barcode_not_found') !== false) {
                await this.addNewItem('sale', barcodeEvent.barcodeInput);
            } else {
                await this.alertDialogService.openDialog({data: {messageLabel: 'CASHREGISTER.SHOWCASE.ARTICLE_NOT_FOUND'}});
            }

            return;
        }

        const priceListStr = `price${this.priceList()}` as `price${number}` & keyof Items;

        for (let found of foundArray) {
            try {
                if (found.quantity && !Number.isFinite(found.quantity)) {
                    await this.alertDialogService.openDialog({data: {messageLabel: 'CASHREGISTER.SHOWCASE.PRICE_UNIT_IS_ZERO'}});
                    continue;
                }

                const item = await this.entityManagerService.items.fetchOneOffline(found.item_id);

                if (!item) {
                    await this.alertDialogService.openDialog({data: {messageLabel: 'CASHREGISTER.SHOWCASE.ARTICLE_NOT_FOUND'}});
                    continue;
                }

                if (found.price != null) {
                    item[priceListStr] = found.price;
                }

                if (Number.isFinite(item[priceListStr])) {
                    this.cashregisterStateService.addItemToSale$.next({
                        item: item,
                        combination_id: found.combination_id,
                        quantity: found.quantity,
                        barcode: found.barcode ?? barcodeEvent.barcodeInput
                    });
                } else {
                    await this.alertDialogService.openDialog({data: {messageLabel: 'CASHREGISTER.SHOWCASE.ARTICLE_NOT_PRESENT_IN_PRICELIST'}});
                }
            } catch (err) {
                console.error(err);
            }
        }

        if (foundArray.length !== foundData.total) {
            await this.alertDialogService.openDialog({data: {messageLabel: 'CASHREGISTER.SHOWCASE.SOME_ITEMS_NOT_FOUND'}});
        }
    }

    protected async onBarcodeSale(barcodeEvent: BarcodeEvent) {
        //Make sure it's a sale event
        if(barcodeEvent.type !== 'sale') {
            return;
        }

        const sale = barcodeEvent.sale;

        if(sale) {
            if(sale.status === 'open') {
                await this.activeSaleService.loadSale(sale.id || sale.uuid!);
            } else {
                //TODO: open in history
            }
        }
    }


    // TILBY-KEYPAD

    protected toggleKeyboard(isOpen: boolean,bySelection?:boolean) {
        this.cashregisterStateService[`${bySelection?'keyboardIsOpenBySelection$':'keyboardIsOpen$'}`].next(isOpen);
        window.localStorage.setItem('cashregister::showKeyboard', `${isOpen}`);
    }
}
